import { ElementRef, useRef } from 'react';
import styled from 'styled-components';

import T from '../../../translate';
import { AppleCalendar, GoogleCalendar, MicrosoftCalendar } from '../../icons';
import { StylableText } from '../../preview/stylable-text';
import { useGlobalStore } from '../../state/globalStore';
import { Padding } from '../../styles/commonStyle';
import { ENTITY_TYPE, FORM_ALIGNMENT } from '../../utils/constants';
import { getCookie, setQueryParams } from '../../utils/helper';
import { closeGateFromPlayer, reportErrorToPlayer } from '../../utils/player-controller';
import { ERROR_CODES } from '../../utils/player-controller/constants';
import { Enum } from '../../utils/types';
import { EventDate } from '../common/EventDate';
import CountdownTimer from '../elements/CountdownTimer';
import { Alignment, Logo, WrapperBox } from '../previewStyles';

export const ConfirmationView = () => {
  const leadCaptureProperties = useGlobalStore((state) => state.leadCapture);
  const leadUuid = useGlobalStore((state) => state.cookie);
  const entityId = useGlobalStore((state) => state.entityId);
  const leadIdFromCookie = getCookie(entityId) || '';
  const entityType = useGlobalStore((state) => state.entityType);
  const {
    logo,
    alignment,
    isEventDateSetToCalendar,
    confirmationPageDescription,
    confirmationPageTitle,
    nonEditableTextStyle: { addToCalendar: addToCalendarStyle },
  } = leadCaptureProperties;
  const confirmTitleRef = useRef<ElementRef<typeof StylableText>>(null);
  const confirmDescRef = useRef<ElementRef<typeof StylableText>>(null);
  const {
    leadCapture: { calendarLinks },
  } = useGlobalStore((store) => store);
  const {
    entity: { schedule },
  } = useGlobalStore((state) => state);

  const onTimeout = () => {
    if (entityType === ENTITY_TYPE.SHOWCASE) {
      return;
    }
    /** When the video timeout hits 0 */
    if (!leadUuid) {
      reportErrorToPlayer({
        message: 'Lead uuid not present for closing the gate',
        code: ERROR_CODES.LEAD_UUID_NOT_PRESENT,
      });
    } else {
      closeGateFromPlayer({ leadUuid });
    }
  };

  return (
    <Alignment align={alignment} addPadding>
      <WrapperBox paddingTop={50} paddingBottom={50}>
        {logo.isActive && (
          <a
            {...(logo.isLinkActive && logo.customLink?.length > 0
              ? { href: logo.customLink, target: '_blank' }
              : {})}
          >
            <Logo src={logo.url} />
          </a>
        )}
        <StylableText ref={confirmTitleRef} content={confirmationPageTitle} />
        <Padding left={4} right={4}>
          <EventDate />
        </Padding>
        <StylableText ref={confirmDescRef} content={confirmationPageDescription} />

        {isEventDateSetToCalendar && schedule && entityType !== ENTITY_TYPE.VIDEO && (
          <>
            <AddToCalenderText color={addToCalendarStyle.color}>
              {T.AddToCalendar}
            </AddToCalenderText>
            <CalendarWrapper align={alignment}>
              <CalLink href={calendarLinks?.google} target="_blank">
                <GoogleCalendar />
              </CalLink>
              <CalLink
                href={
                  calendarLinks?.outlook
                    ? setQueryParams(calendarLinks?.outlook, { leadId: leadIdFromCookie })
                    : undefined
                }
                target="_blank"
              >
                <MicrosoftCalendar />
              </CalLink>
              <CalLink
                href={
                  calendarLinks?.ical
                    ? setQueryParams(calendarLinks?.ical, { leadId: leadIdFromCookie })
                    : undefined
                }
                target="_blank"
              >
                <AppleCalendar />
              </CalLink>
            </CalendarWrapper>
          </>
        )}
      </WrapperBox>
      {(entityType === ENTITY_TYPE.VIDEO || entityType === ENTITY_TYPE.SHOWCASE) && (
        <CountDownWrapper>
          <CountdownTimer duration={5000} onTimeout={onTimeout} />
        </CountDownWrapper>
      )}
    </Alignment>
  );
};

const CalendarWrapper = styled.div<{
  align: Enum<typeof FORM_ALIGNMENT>;
}>`
  display: flex;
  gap: 32px;
  justify-content: ${({ align }) =>
    align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center'};
  border: solid 2px transparent;
  padding-left: 5px;
  padding-right: 5px;
  // Overrider alignment in case of mobile preview and default to center
  @media only screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const AddToCalenderText = styled.p<{ color: string }>`
  margin: 0;
  margin-bottom: 6px;
  padding-left: 5px;
  padding-right: 5px;
  border: solid 2px transparent;
  color: ${({ color }) => color};
`;

const CalLink = styled.a`
  cursor: pointer;
`;

const CountDownWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
`;
